<script setup lang="ts">
  import { Checkbox, CheckboxSize, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed } from 'vue';
  import { splitDestinationName, type NameMatch } from '../lib/matchDestinationName';

  interface DestinationMobileCheckRowProps {
    name: string;
    sub?: string;
    checked: boolean;
    highlight?: string;
  }

  const props = withDefaults(defineProps<DestinationMobileCheckRowProps>(), {
    sub: '',
    highlight: '',
  });

  interface DestinationMobileCheckRowEmits {
    (e: 'toggle'): void;
  }

  const emit = defineEmits<DestinationMobileCheckRowEmits>();

  const match = computed<NameMatch | null>(() => splitDestinationName(props.name, props.highlight));

  const onToggle = (): void => {
    emit('toggle');
  };
</script>

<template>
  <div
    class="destination-mobile-check"
    data-testid="destination-mobile-check"
    @click="onToggle"
  >
    <Checkbox
      class="destination-mobile-check__checkbox"
      data-testid="destination-mobile-check-checkbox"
      :size="CheckboxSize.L"
      :mobile="true"
      :model-value="checked"
      @update:modelValue="onToggle"
    />

    <span class="destination-mobile-check__body">
      <Typo
        class="destination-mobile-check__name"
        :variant="TypoVariant.BodyLRegular"
        tag="span"
      >
        <template v-if="match">
          <span>{{ match.before }}</span>
          <b>{{ match.match }}</b>
          <span>{{ match.after }}</span>
        </template>
        <template v-else>{{ name }}</template>
      </Typo>
      <Typo
        v-if="sub"
        class="destination-mobile-check__sub"
        :variant="TypoVariant.BodyXSRegular"
        tag="span"
      >
        {{ sub }}
      </Typo>
    </span>
  </div>
</template>

<style scoped lang="scss">
  .destination-mobile-check {
    align-items: flex-start;
    box-sizing: border-box;
    display: flex;
    gap: var(--spacing-s);
    min-height: 40px;
    padding: var(--spacing-xs) var(--spacing-m);

    &__checkbox {
      flex-shrink: 0;
    }

    &__body {
      display: flex;
      flex: 1 1 auto;
      flex-direction: column;
      min-width: 0;
    }

    &__name {
      color: var(--color-text-primary);

      b {
        color: var(--color-brand-secondary);
        font-weight: 600;
      }
    }

    &__sub {
      color: var(--color-text-secondary);
    }
  }
</style>
